import { ref, onMounted, onUnmounted } from 'vue';
import { alertController } from '@ionic/vue';
import { useRouter } from 'vue-router';
import { useStore } from '@/stores/main';

export function useSimpleSessionManager() {
  const store = useStore();
  const router = useRouter();
  
  const isActive = ref(false);
  const warningShown = ref(false);
  let activityTimer: number | null = null;
  let warningTimer: number | null = null;

  const SESSION_TIMEOUT = 4 * 60 * 60 * 1000; // 4 hours in milliseconds
  const WARNING_TIME = 15 * 60 * 1000; // 15 minutes before timeout
  const CHECK_INTERVAL = 2 * 60 * 1000; // Check every 2 minutes

  const updateActivity = () => {
    const now = Date.now();
    store.lastActivity = now;
    store.sessionWarningShown = false;
    warningShown.value = false;
    
    console.log('👆 User activity detected - resetting session timer');
    startSessionTimer();
  };

  const startSessionTimer = () => {
    // Clear existing timers
    if (activityTimer) clearTimeout(activityTimer);
    if (warningTimer) clearTimeout(warningTimer);
    
    console.log('🚀 Starting session timer - 4 hours');
    
    // Set warning timer (15 minutes before timeout)
    warningTimer = setTimeout(() => {
      if (!warningShown.value && !store.sessionWarningShown) {
        showSessionWarning();
      }
    }, SESSION_TIMEOUT - WARNING_TIME);
    
    // Set logout timer
    activityTimer = setTimeout(() => {
      console.log('⏰ Session timeout - logging out');
      handleLogout();
    }, SESSION_TIMEOUT);
  };

  const showSessionWarning = async () => {
    if (warningShown.value || store.sessionWarningShown) return;
    
    warningShown.value = true;
    store.sessionWarningShown = true;
    
    console.log('⚠️ Showing session warning');
    
    const alert = await alertController.create({
      header: 'Sessionen håller på att gå ut',
      message: 'Din session kommer att gå ut om 15 minuter på grund av inaktivitet. Spara ditt arbete och fortsätt för att stanna inloggad.',
      buttons: [
        {
          text: 'Fortsätt arbeta',
          handler: () => {
            console.log('✅ User chose to continue working');
            updateActivity();
          }
        },
        {
          text: 'Logga ut nu',
          handler: () => {
            console.log('🚪 User chose to logout now');
            handleLogout();
          }
        }
      ],
      backdropDismiss: false
    });
    
    await alert.present();
  };

  const handleLogout = async () => {
    console.log('🚪 Logging out due to session timeout');
    
    // Clear all timers
    if (activityTimer) clearTimeout(activityTimer);
    if (warningTimer) clearTimeout(warningTimer);
    
    // Reset session state
    store.sessionWarningShown = false;
    warningShown.value = false;
    isActive.value = false;
    
    // Clear user data
    store.loggedIn = false;
    store.token = '';
    store.userId = 0;
    store.firstName = '';
    store.lastName = '';
    store.email = '';
    store.company = '';
    store.profile = null;
    
    // Navigate to login
    router.push('/login');
  };

  const checkSessionValidity = () => {
    if (!store.loggedIn || !store.token) {
      console.log('❌ No valid session found');
      return false;
    }
    
    const now = Date.now();
    const lastActivity = store.lastActivity || 0;
    const timeSinceActivity = now - lastActivity;
    
    if (timeSinceActivity >= SESSION_TIMEOUT) {
      console.log('⏰ Session expired - time since last activity:', Math.round(timeSinceActivity / 1000 / 60), 'minutes');
      handleLogout();
      return false;
    }
    
    console.log('✅ Session valid - time since last activity:', Math.round(timeSinceActivity / 1000 / 60), 'minutes');
    return true;
  };

  const startSessionManagement = () => {
    console.log('🔧 Starting session management...');
    console.log('📊 Store state:', { loggedIn: store.loggedIn, hasToken: !!store.token });
    
    // For testing, start even without login
    console.log('✅ Starting session management for testing (bypassing login check)');
    isActive.value = true;
    startSessionTimer();
  };

  const stopSessionManagement = () => {
    console.log('🛑 Stopping session management');
    isActive.value = false;
    
    if (activityTimer) {
      clearTimeout(activityTimer);
      activityTimer = null;
    }
    
    if (warningTimer) {
      clearTimeout(warningTimer);
      warningTimer = null;
    }
  };

  // Activity event listeners
  const activityEvents = [
    'mousedown', 'mousemove', 'keypress', 'scroll', 
    'touchstart', 'click', 'keydown'
  ];

  const addActivityListeners = () => {
    activityEvents.forEach(event => {
      document.addEventListener(event, updateActivity, true);
    });
  };

  const removeActivityListeners = () => {
    activityEvents.forEach(event => {
      document.removeEventListener(event, updateActivity, true);
    });
  };

  // Periodic session check
  let sessionCheckInterval: number | null = null;

  const startPeriodicCheck = () => {
    sessionCheckInterval = setInterval(() => {
      if (store.loggedIn && store.token) {
        checkSessionValidity();
      }
    }, CHECK_INTERVAL);
  };

  const stopPeriodicCheck = () => {
    if (sessionCheckInterval) {
      clearInterval(sessionCheckInterval);
      sessionCheckInterval = null;
    }
  };

  onMounted(() => {
    console.log('🚀 Session manager mounted');
    console.log('🔍 Checking session validity...');
    
    // For testing, always start session management
    console.log('✅ Starting session management for testing');
    startSessionManagement();
    addActivityListeners();
    startPeriodicCheck();
  });

  onUnmounted(() => {
    stopSessionManagement();
    removeActivityListeners();
    stopPeriodicCheck();
  });

  return {
    isActive,
    warningShown,
    updateActivity,
    startSessionManagement,
    stopSessionManagement,
    checkSessionValidity
  };
}
